import type {
  BuilderAnswerOption,
  BuilderAnswerType,
  BuilderDetail,
  BuilderQuestion,
  BuilderSection,
  RawBuilderDetail,
  RawQuestion,
  RawSection,
} from '@/api/questionnaires/types';
import { FIELD_TYPE_TO_ANSWER_TYPE } from '../-constants/answerTypes';

/** Coerces the server's wire-format answer type onto our internal enum.
 *  Legacy questionnaires send `SHORT_TEXT` / `LONG_TEXT` / `YES_NO` /
 *  `NUMBER` / `SELECT` (ALL_CAPS) — these need to be mapped to our
 *  Pascal-case identifiers (`ShortText`, etc.). Other legacy ALL_CAPS
 *  keys (`DATE`, `DOCUMENTS`, `AUDIT`, `CERTIFICATE`, `NUMBER_UOM`,
 *  `CHECK_BOXES`) already match our enum, so they pass through. */
function coerceAnswerType(raw: string | undefined): BuilderAnswerType {
  const value = raw ?? 'ShortText';
  return FIELD_TYPE_TO_ANSWER_TYPE[value] ?? (value as BuilderAnswerType);
}

/** Reverse of `coerceAnswerType` — converts our Pascal-case internal enum
 *  back to the server's ALL_CAPS wire format on save. Required so existing
 *  legacy questions (stored as `SHORT_TEXT` / `LONG_TEXT` / `NUMBER` /
 *  `SELECT` / `YES_NO`) round-trip identically and the backend's
 *  active-questionnaire diff guard (`existingQuestion.questionTypeId !==
 *  newQuestion.questionTypeId`) doesn't trip. Also keeps new custom
 *  questions consistent with what legacy writes. */
const ANSWER_TYPE_TO_WIRE: Record<string, string> = {
  ShortText: 'SHORT_TEXT',
  LongText: 'LONG_TEXT',
  YesOrNo: 'YES_NO',
  Number: 'NUMBER',
  Select: 'SELECT',
};

function toWireAnswerType(answerType: string): string {
  return ANSWER_TYPE_TO_WIRE[answerType] ?? answerType;
}

/** Backfills `label` on options that came from the legacy persistence
 *  layer with only `text` / `title` / `name` / `value` / `key`. Without
 *  this the option-list previews render empty rows. The legacy fields
 *  stay on the object so a subsequent save round-trips them. */
function normalizeOption(raw: BuilderAnswerOption): BuilderAnswerOption {
  const fallback =
    raw.text ??
    raw.title ??
    raw.name ??
    (typeof raw.value === 'string' ? raw.value : undefined) ??
    raw.key ??
    '';
  return {
    ...raw,
    label: raw.label && raw.label.length > 0 ? raw.label : fallback,
  };
}

/** Server → internal section. Accepts either the server's shape
 *  (`id, name`) or our shape (`sectionId, sectionName`) so partial
 *  responses round-trip cleanly. */
export function normalizeSection(raw: RawSection): BuilderSection {
  return {
    sectionId: raw.id ?? raw.sectionId ?? '',
    sectionName: raw.name ?? raw.sectionName ?? '',
    ...(raw.description ? { description: raw.description } : {}),
  };
}

/** Server → internal question. Flattens `meta.*` into our top-level
 *  fields and reads from either set of legacy field names. */
export function normalizeQuestion(raw: RawQuestion): BuilderQuestion {
  const meta = (raw.meta ?? {}) as {
    options?: BuilderAnswerOption[];
    unit?: string;
    isCompulsory?: number | boolean;
    required?: number | boolean;
    isGlobal?: boolean | string;
    attachmentFileId?: string | null;
    attachmentFileName?: string | null;
    attachmentFileContentType?: string | null;
    attachmentFileUrl?: string | null;
    attachmentFileOriginalUrl?: string | null;
  };
  // `isCompulsory` is the persisted name (lives in meta after save). Some
  // endpoints promote it to the top level, others leave it nested. Also
  // accept the newer `required` alias in case it ever appears.
  const required = Boolean(
    raw.isCompulsory ??
      meta.isCompulsory ??
      (raw as { required?: number | boolean }).required ??
      meta.required ??
      false,
  );
  const rawOptions = raw.options ?? meta.options;
  const options = rawOptions ? rawOptions.map(normalizeOption) : undefined;
  // Several signals can mark a question as "global". Without the fallbacks
  // below, `isGlobal` is lost the moment we save and reload — `serializeQuestion`
  // does not persist `isGlobal` itself, so we must infer it.
  //   1. Explicit `raw.isGlobal: true | 'GLOBAL'` (legacy builder response)
  //   2. Same key nested in meta
  //   3. `isCustom === false` (set by our serializer on save — any non-custom
  //      question is datapoint-bound)
  //   4. `retracedDatapointId` present (final fallback)
  const rawIsCustomIsTrue =
    raw.isCustom === true ||
    raw.isCustom === 'CUSTOM' ||
    raw.isCustom === 'custom';
  const isGlobal =
    raw.isGlobal === true ||
    (typeof raw.isGlobal === 'string' && raw.isGlobal === 'GLOBAL') ||
    meta.isGlobal === true ||
    (typeof meta.isGlobal === 'string' && meta.isGlobal === 'GLOBAL') ||
    (raw.isCustom !== undefined && !rawIsCustomIsTrue) ||
    Boolean(raw.retracedDatapointId);
  // Legacy backend uses both `sync` and `isSyncAllowed` depending on the
  // path. Treat either as the responder-facing "Update value on profile"
  // gate. The answer-request endpoint doesn't ship the flag — legacy
  // hydrates it from a separate company-profile fetch and defaults to
  // `true` for any non-custom question (see
  // useGlobalDatapointSyncPermission in the legacy frontend). Mirror
  // that default here so every non-custom question shows the toggle
  // unless the wire explicitly sets the flag to false.
  const rawSync = raw.isSyncAllowed ?? raw.sync;
  const isSyncAllowed =
    rawSync === undefined ? !rawIsCustomIsTrue : Boolean(rawSync);
  const rawTypeFromWire = raw.type ?? raw.questionTypeId ?? raw.answerType;
  // Snapshot the exact four fields the backend's diff guard compares.
  // Capture the title without any trim/fallback; isCompulsory + options
  // come straight from `meta` so we can compare against what the backend
  // sees as `JSON.parse(existingQuestion.meta)`.
  const rawDiffSnapshot =
    rawTypeFromWire && (raw.id ?? raw.questionId)
      ? {
          title: raw.title ?? raw.questionTitle ?? '',
          questionTypeId: rawTypeFromWire,
          isCompulsory: meta.isCompulsory ?? raw.isCompulsory ?? 0,
          options: meta.options ?? raw.options ?? [],
        }
      : undefined;
  return {
    questionId: raw.id ?? raw.questionId ?? '',
    sectionId: raw.sectionId ?? raw.questionnaireSectionId ?? '',
    questionTitle: raw.title ?? raw.questionTitle ?? '',
    questionDescription: raw.description ?? raw.questionDescription ?? '',
    answerType: coerceAnswerType(rawTypeFromWire),
    // Preserve the exact server-side string so save round-trips it back
    // verbatim — see BuilderQuestion.rawAnswerType for the rationale.
    ...(rawTypeFromWire ? { rawAnswerType: rawTypeFromWire } : {}),
    ...(rawDiffSnapshot ? { rawDiffSnapshot } : {}),
    ...(options ? { options } : {}),
    ...(rawIsCustomIsTrue ? { isCustom: true } : {}),
    ...(isGlobal ? { isGlobal: true } : {}),
    ...(isSyncAllowed ? { isSyncAllowed: true } : {}),
    ...((raw.unit ?? meta.unit)
      ? { unit: (raw.unit ?? meta.unit) as string }
      : {}),
    isRequired: required,
    ...(raw.retracedDatapointId
      ? { retracedDatapointId: raw.retracedDatapointId }
      : {}),
    ...(raw.retracedTemplateId
      ? { retracedTemplateId: raw.retracedTemplateId }
      : {}),
    ...((raw.attachmentFileId ?? meta.attachmentFileId)
      ? {
          attachmentFileId: (raw.attachmentFileId ?? meta.attachmentFileId) as
            | string
            | null,
        }
      : {}),
    ...((raw.attachmentFileName ?? meta.attachmentFileName)
      ? {
          attachmentFileName: (raw.attachmentFileName ??
            meta.attachmentFileName) as string | null,
        }
      : {}),
    ...((raw.attachmentFileContentType ?? meta.attachmentFileContentType)
      ? {
          attachmentFileContentType: (raw.attachmentFileContentType ??
            meta.attachmentFileContentType) as string | null,
        }
      : {}),
    ...((raw.attachmentFileUrl ?? meta.attachmentFileUrl)
      ? {
          attachmentFileUrl: (raw.attachmentFileUrl ??
            meta.attachmentFileUrl) as string | null,
        }
      : {}),
    ...((raw.attachmentFileOriginalUrl ?? meta.attachmentFileOriginalUrl)
      ? {
          attachmentFileOriginalUrl: (raw.attachmentFileOriginalUrl ??
            meta.attachmentFileOriginalUrl) as string | null,
        }
      : {}),
  };
}

export function normalizeBuilderDetail(raw: RawBuilderDetail): BuilderDetail {
  const sections = (raw.sections ?? []).map(normalizeSection);
  const rawQuestions = (raw.questions ?? []).map(normalizeQuestion);
  // Older questionnaires stored questions without a section (or pointing at a
  // section that's since been removed). Without this fallback they load into
  // the form but never render — they fail the `q.sectionId === section.sectionId`
  // filter every section uses. Synthesize a section if there are none, then
  // reassign any orphans to the first section.
  const hasOrphans = rawQuestions.some(
    q => !q.sectionId || !sections.find(s => s.sectionId === q.sectionId),
  );
  if (hasOrphans && sections.length === 0) {
    sections.push({ sectionId: 'section-default', sectionName: '' });
  }
  const firstSectionId = sections[0]?.sectionId ?? '';
  const questions = rawQuestions.map(q =>
    !q.sectionId || !sections.find(s => s.sectionId === q.sectionId)
      ? { ...q, sectionId: firstSectionId }
      : q,
  );
  return {
    id: raw.id ?? '',
    name: raw.name ?? '',
    description: raw.description ?? '',
    status: raw.status ?? 'DRAFT',
    isTemplate: raw.isTemplate ?? false,
    sections,
    questions,
    ...(raw.dueDate !== undefined ? { dueDate: raw.dueDate } : {}),
    ...(raw.createdAt !== undefined ? { createdAt: raw.createdAt } : {}),
    ...(raw.updatedAt !== undefined ? { updatedAt: raw.updatedAt } : {}),
    ...(raw.updatedByUserName !== undefined
      ? { updatedByUserName: raw.updatedByUserName }
      : {}),
    ...(raw.updatedByCompanyName !== undefined
      ? { updatedByCompanyName: raw.updatedByCompanyName }
      : {}),
    ...(raw.statistics !== undefined ? { statistics: raw.statistics } : {}),
  };
}

/** Internal → server section payload. Server requires `{id, name, sortOrder}`
 *  with `name.minLength: 1`, so we fall back to "Section N" for empty
 *  inline-edit drafts. */
export function serializeSection(s: BuilderSection, index: number) {
  const name = s.sectionName.trim() || `Section ${index + 1}`;
  return {
    id: s.sectionId,
    name,
    sortOrder: index + 1,
    description: s.description ?? null,
  };
}

/** Mirror legacy `transformQuestionsToPayload` option shape so the backend's
 *  active-questionnaire diff guard (`_.isEqual(_.sortBy(existing.options),
 *  _.sortBy(new.options))`) matches stored values exactly. Legacy populates
 *  `key`, `value` (and `text` for custom questions) on every option; without
 *  this every save against an active questionnaire trips
 *  `cannot_edit_question_for_active_questionnaire`. */
export function serializeOption(
  o: Record<string, unknown>,
  isCustom: boolean,
): Record<string, unknown> {
  const opt: Record<string, unknown> = { ...o };
  const label = (o.label ?? o.text ?? o.value) as string | undefined;
  if (opt.key === undefined) opt.key = o.key ?? o.optionId ?? o.id ?? label;
  if (opt.value === undefined) opt.value = o.value ?? label ?? null;
  if (isCustom) {
    if (opt.text === undefined) opt.text = label;
  } else if (opt.text === undefined) {
    delete opt.text;
  }
  delete opt.meta;
  // `label` is a v2-internal display field — `normalizeOption` injects it
  // for the builder UI but the persistence layer never stored it. Sending
  // it back makes `_.isEqual(stored, sent)` fail and the backend rejects
  // the save with `cannot_edit_question_for_active_questionnaire`.
  delete opt.label;
  return opt;
}

/** Internal → server question payload. Mirrors legacy
 *  `transformQuestionsToPayload`: meta carries only `options`, `isCompulsory`,
 *  and (when set) `unit` and attachment fields. The backend's active-
 *  questionnaire diff guard compares `meta.options` and `meta.isCompulsory`
 *  directly against the stored JSON, so sending extra null-valued keys
 *  (`unit: null`, `attachmentFileId: null`, …) was producing meta blobs that
 *  don't match what legacy ever wrote — fine for the diff itself, but it
 *  poisoned the round-trip for any subsequent save. Now we omit fields
 *  rather than send nulls. */
export function serializeQuestion(q: BuilderQuestion, index: number) {
  const title = q.questionTitle.trim() || 'Untitled question';
  // Legacy backend writes a `files` row for every question whose meta has
  // `attachmentFileId`, reading `originalUrl` (falls back to `url.split(...)`
  // which 500s on undefined). Only forward the attachment fields when we
  // have at least one URL — otherwise the file row insert blows up.
  const originalUrl =
    q.attachmentFileOriginalUrl ?? q.attachmentFileUrl ?? null;
  const hasUsableAttachment = Boolean(q.attachmentFileId && originalUrl);
  const isCustom = !q.retracedDatapointId;
  const options = q.options
    ? q.options.map(o =>
        serializeOption(o as unknown as Record<string, unknown>, isCustom),
      )
    : [];
  const meta: Record<string, unknown> = {
    options,
    isCompulsory: q.isRequired ? 1 : 0,
  };
  if (q.unit) meta.unit = q.unit;
  if (hasUsableAttachment) {
    meta.attachmentFileId = q.attachmentFileId;
    meta.attachmentFileName = q.attachmentFileName;
    meta.attachmentFileContentType = q.attachmentFileContentType;
    meta.attachmentFileUrl = q.attachmentFileUrl;
    meta.attachmentFileOriginalUrl = originalUrl;
  }
  return {
    id: q.questionId,
    sectionId: q.sectionId,
    title,
    description: q.questionDescription || null,
    // Prefer the verbatim raw value the server returned. For new questions
    // added in this session there's no raw; fall back to the Pascal→ALL_CAPS
    // map so legacy DB conventions are honored.
    questionTypeId: q.rawAnswerType ?? toWireAnswerType(q.answerType),
    isCustom,
    retracedDatapointId: q.retracedDatapointId ?? null,
    sortOrder: index + 1,
    meta,
  };
}
